<script setup lang="ts">
import {
    closestRoleFromTyped,
    type KnownWorkRole,
    reportingStyleFor,
    roleAnswerFromTyped,
    SOMETHING_ELSE,
    SOMETHING_ELSE_LABEL,
    TECHNICAL_LEVEL_LABELS,
    TECHNICAL_LEVELS,
    type TechnicalLevel,
    WORK_ROLE_LABELS,
    WORK_ROLES,
    type WorkProfile,
    type WorkRole,
    workAnswerFromTyped,
} from '@zerohuman/shared'
import {
    Button,
    Input,
    ZhButton,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStepBar,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, type CatalogTeamOffer, type OnboardingStateView } from '@/api'
import SetupNamesForm from '@/components/SetupNamesForm.vue'
import { useUiMode } from '@/composables/useUiMode'
import { backToSetupChat, setupChatFrom } from '@/lib/backToSetupChat'
import { GUIDED_STEPS, guidedScreenAt, HAND_OVER_TITLES } from '@/lib/guidedSetup'
import { MORE_TOOLS_TITLE } from '@/lib/moreTools'
import { ACCOUNT_STEP_PATH } from '@/lib/onboardingGate'
import { onboardingSession } from '@/lib/onboardingSession'
import {
    type NamesAnswers,
    offeredKinds,
    SET_UP_WITH_COO,
    SKIP_SETUP,
    setupSkippable,
    TELL_YOUR_COO,
} from '@/lib/setupChat'
import OnboardingMoreToolsView from '@/views/OnboardingMoreToolsView.vue'
import OnboardingTeamSetupView from '@/views/OnboardingTeamSetupView.vue'

/**
 * The guided setup shell (#1742): the step-by-step path, one click from the setup conversation. It reads the one
 * server-held setup state on the guided path, so it opens at the step the server says is current with every answer
 * kept, and draws the step bar at it.
 *
 * - Kind of work and About you save through the same `work_profile` save the chat uses. Anything already answered in
 *   the chat is filled in, and Back never loses what was entered.
 * - A typed role is matched to the closest known role (`closestRoleFromTyped`), shown selected with its preview line,
 *   and can be changed before continuing. Nothing is saved until Continue.
 * - Your team offers the teams the server proposes for the kind of work (#2216), the first marked Recommended, and
 *   Continue saves the one picked as `team_proposal`. Names is the names form both paths share (`SetupNamesForm`):
 *   Continue or Skip saves `names`, which starts the assembly, so the team-setup screen with its log follows. A
 *   refused start (the name is taken, no room for the teammates…) shows the server's plain words on the form, which
 *   keeps what was typed. Back from Names shows Your team again and saves nothing until Continue.
 * - Team setup is the existing team-setup screen, drawn `embedded`: this shell's own "Set up with your COO instead" is
 *   its way out, so it draws no second one (#2154). A step with no guided screen yet shows its title and hands over to
 *   the COO, never a blank page.
 * - "Set up with your COO instead" is on every screen: it moves setup back to the chat and opens the setup
 *   conversation at the same step. The skip link is on the first screen, once the AI account step is complete.
 */
const COO_HAND_OVER = 'Continue this step with your COO'

const route = useRoute()
const router = useRouter()
const { setMode } = useUiMode()
const session = onboardingSession()

const state = ref<OnboardingStateView | null>(null)
const failure = ref<string | null>(null)
const busy = ref(false)
/** The kinds of work the setup conversation's greeting offered, when the person came from it and it could be read. */
const chatKinds = ref<string[]>([])
/**
 * The kinds of work Kind of work offers: the setup conversation's own when the person came from it, so both paths
 * show the same chips, else the Catalog's as the server serves them at this step (#1742). A direct link, a bookmark
 * or a conversation that cannot be read still offers every kind, beside "Something else".
 */
const kinds = computed(() => (chatKinds.value.length > 0 ? chatKinds.value : (state.value?.kindsOfWork ?? [])))
/** Back from About you: Kind of work again, with what was saved filled in. */
const editingWork = ref(false)
/** Back from Names: Your team again, with the team picked still selected, saving nothing until Continue. */
const editingTeam = ref(false)

/** The setup conversation the person came from, so the way back is that same conversation. */
const fromChat = computed(() => setupChatFrom(route.query.chat))

const savedProfile = computed((): Partial<WorkProfile> => {
    const saved = state.value?.answers['work_profile']
    return typeof saved === 'object' && saved !== null ? (saved as Partial<WorkProfile>) : {}
})

const at = computed(() => guidedScreenAt(state.value?.currentStep ?? null, state.value?.answers['work_profile']))
const screen = computed(() => {
    if (state.value?.status === 'complete') {
        return 'done'
    }
    // #1942: once Team setup is saved the server offers the optional tools step, until it is skipped or done.
    if (state.value?.moreTools) {
        return 'more_tools'
    }
    if (editingWork.value && at.value.screen === 'about_you') {
        return 'work'
    }
    if (editingTeam.value && at.value.screen === 'names') {
        return 'team'
    }
    // #2216: a step the server serves nothing to answer with (no team to offer, no template to name) is done with
    // the COO, as a step with no screen is: the screen would otherwise be empty.
    if ((at.value.screen === 'team' && offers.value.length === 0) || (at.value.screen === 'names' && !namesFor.value)) {
        return 'hand_over'
    }
    return at.value.screen
})
const barStep = computed(() => {
    if (screen.value === 'work') {
        return 'work'
    }
    return screen.value === 'team' ? 'team' : (at.value.bar ?? '')
})
const handOverTitle = computed(
    () =>
        HAND_OVER_TITLES[state.value?.currentStep ?? ''] ??
        GUIDED_STEPS.find(step => step.id === at.value.bar)?.label ??
        'This step',
)
const offerSkip = computed(() => screen.value === 'work' && setupSkippable(state.value))

// Your team (#2216): the teams the server proposes, recommended first, then the candidates for custom work.
const offers = computed((): CatalogTeamOffer[] => {
    const proposal = state.value?.teamProposal
    return proposal ? [...proposal.recommended, ...proposal.candidates] : []
})
const recommendedId = computed(() => state.value?.teamProposal?.recommended[0]?.id ?? null)
const pickedTeam = ref<string | null>(null)

// Names (#2216): the proposed template, and the server's plain-words refusal of the last save, shown on the form.
const namesFor = computed(() => state.value?.namesFor?.template ?? null)
const namesFailure = ref<string | null>(null)

// Kind of work.
const kind = ref<string | null>(null)
const otherOpen = ref(false)
const otherText = ref('')
const workAnswer = computed(() => {
    if (otherOpen.value) {
        return workAnswerFromTyped(otherText.value, kinds.value)
    }
    return kind.value ? { kind: kind.value } : null
})

// About you.
const typedRole = ref('')
const pickedRole = ref<KnownWorkRole | null>(null)
const technicalLevel = ref<TechnicalLevel | null>(null)
/**
 * The role About you would save, by the chat's rules (`roleGuessFor`), so both paths save the same answer:
 * - a picked chip that differs from what the typed role matched saves that chip alone;
 * - typed text naming a role's chip (`Engineer`) is that chip: the plain role, with no words and no mapped role;
 * - any other typed role is the known role it is closest to, with the words the profile keeps (at most
 *   `WORK_PROFILE_TEXT_MAX` characters, as the chat keeps). Picking the chip it already matched accepts that match and
 *   keeps the words, as the chat's `Yes, <role>` does.
 */
const role = computed((): WorkRole | null => {
    const matched = closestRoleFromTyped(typedRole.value)
    if (pickedRole.value && pickedRole.value !== matched) {
        return { role: pickedRole.value }
    }
    const answer = roleAnswerFromTyped(typedRole.value)
    if (!answer || !matched) {
        return null
    }
    return answer.role === 'other' ? { role: matched, text: answer.text } : { role: answer.role }
})
const preview = computed(() => (role.value ? `Your COO will report in ${reportingStyleFor(role.value)}.` : null))

onMounted(async () => {
    await Promise.all([load(), loadKinds()])
})

async function load(): Promise<void> {
    try {
        take(await api.onboarding('guided', session))
    } catch {
        failure.value = 'Could not load where setup is. Reload the page to try again.'
    }
}

/** The kinds of work the setup conversation offered: the guided screen offers the same chips. */
async function loadKinds(): Promise<void> {
    if (!fromChat.value) {
        return
    }
    try {
        chatKinds.value = offeredKinds((await api.chat(fromChat.value)).messages)
    } catch {
        // Without them, the Catalog's kinds the setup state carries are offered instead.
    }
}

/** A setup state from the server, with what it holds filled in on the screens. */
function take(next: OnboardingStateView): void {
    state.value = next
    const saved = savedProfile.value
    if (saved.work) {
        otherOpen.value = saved.work.kind === SOMETHING_ELSE
        kind.value = saved.work.kind === SOMETHING_ELSE ? null : saved.work.kind
        otherText.value = 'text' in saved.work ? saved.work.text : ''
    }
    if (saved.role) {
        const text = 'text' in saved.role ? (saved.role.text ?? '') : ''
        typedRole.value = text
        pickedRole.value = saved.role.role !== 'other' && !text ? saved.role.role : null
    }
    technicalLevel.value = saved.technicalLevel ?? technicalLevel.value
    // The team picked: the one saved, else the one still selected, else the first the server recommends.
    const proposed = next.answers['team_proposal']
    const savedTeam =
        typeof proposed === 'object' && proposed !== null
            ? (proposed as Record<string, unknown>)['catalogTeamId']
            : null
    const known = (id: unknown) => (typeof id === 'string' && offers.value.some(offer => offer.id === id) ? id : null)
    pickedTeam.value = known(savedTeam) ?? known(pickedTeam.value) ?? offers.value[0]?.id ?? null
}

/** One line of an offer: how many teammates, and the roles they hold. */
function teammateLine(offer: CatalogTeamOffer): string {
    const count = `${offer.teammates} ${offer.teammates === 1 ? 'teammate' : 'teammates'}`
    return offer.roles.length > 0 ? `${count}: ${offer.roles.join(', ')}` : count
}

/** Continue on Your team: the picked team saved as the proposal, so the server serves its template to name. */
async function continueTeam(): Promise<void> {
    const catalogTeamId = pickedTeam.value
    if (!catalogTeamId) {
        return
    }
    await attempt('Could not save that choice.', async () => {
        take(await api.saveOnboardingStep('team_proposal', { catalogTeamId }, 'guided', session))
        editingTeam.value = false
    })
}

/**
 * Continue or Skip on Names: the names saved, or `{}` to keep the template's name and the role titles. The server
 * starts the assembly on the save, so the state taken in moves to the team-setup screen and its log. A refusal is
 * shown on the form, which keeps what was typed to change and save again.
 */
async function saveNames(answers: NamesAnswers | null): Promise<void> {
    busy.value = true
    namesFailure.value = null
    try {
        take(await api.saveOnboardingStep('names', answers ? { ...answers } : {}, 'guided', session))
    } catch (error) {
        namesFailure.value = error instanceof Error ? error.message : 'Could not save those names.'
    } finally {
        busy.value = false
    }
}

function pickKind(value: string): void {
    kind.value = value
    otherOpen.value = false
}

function openOther(): void {
    otherOpen.value = true
    kind.value = null
}

/** Typing a role again starts from what is typed: the closest role is selected until another is picked. */
function typeRole(value: string | number): void {
    typedRole.value = String(value)
    pickedRole.value = null
}

/** Runs a server action behind the busy flag, clearing the last failure; a failure shows its message, else `fallback`. */
async function attempt(fallback: string, action: () => Promise<void>): Promise<void> {
    busy.value = true
    failure.value = null
    try {
        await action()
    } catch (error) {
        failure.value = error instanceof Error ? error.message : fallback
    } finally {
        busy.value = false
    }
}

async function save(answers: Partial<WorkProfile>): Promise<void> {
    await attempt('Could not save that answer.', async () => {
        take(await api.saveOnboardingStep('work_profile', { ...answers }, 'guided', session))
        editingWork.value = false
    })
}

async function continueWork(): Promise<void> {
    if (workAnswer.value) {
        await save({ work: workAnswer.value })
    }
}

async function continueAboutYou(): Promise<void> {
    const answers: Partial<WorkProfile> = {}
    if (role.value) {
        answers.role = role.value
    }
    if (technicalLevel.value) {
        answers.technicalLevel = technicalLevel.value
    }
    if (Object.keys(answers).length > 0) {
        await save(answers)
    }
}

/**
 * "Set up with your COO instead", and a step's hand-over: setup moves back to the chat, and the setup conversation
 * opens at the same step. The conversation the person came from when there is one, else a new setup conversation.
 * The standalone team-setup screen leaves the same way (`backToSetupChat`).
 */
async function backToChat(): Promise<void> {
    await attempt('Could not open the setup conversation.', () => backToSetupChat(router, fromChat.value))
}

/** The skip link: setup skipped on the server with every answer kept, Advanced mode on, and the dashboard. */
async function skip(): Promise<void> {
    await attempt('Could not skip setup.', async () => {
        state.value = await api.skipOnboarding('guided', session)
        setMode('advanced')
        await router.push('/')
    })
}
</script>

<template>
    <section class="flex w-full flex-col gap-6 p-6" data-testid="onboarding-guided">
        <header class="flex flex-wrap items-start justify-between gap-4">
            <ZhPageTitle kicker="Setup">Step by step</ZhPageTitle>
            <Button
                v-if="state && screen !== 'done'"
                type="button"
                variant="link"
                data-testid="guided-back-to-chat"
                :disabled="busy"
                @click="backToChat"
            >
                {{ SET_UP_WITH_COO }}
            </Button>
        </header>
        <ZhStepBar :steps="GUIDED_STEPS" :current="barStep" />
        <p
            v-if="state && screen === 'more_tools'"
            class="text-muted-foreground text-sm"
            data-testid="guided-optional-step"
        >
            Now: {{ MORE_TOOLS_TITLE }}. It is not one of the setup steps above, so you can skip it.
        </p>
        <p v-if="failure" role="alert" class="text-sm" data-testid="guided-failure">{{ failure }}</p>
        <p v-if="state?.status === 'skipped'" class="text-muted-foreground text-sm" data-testid="guided-skipped">
            You skipped setup for now. Everything you entered is kept: carry on from here whenever you like.
        </p>

        <ZhPanel v-if="state && screen === 'work'" class="space-y-4" data-testid="guided-work">
            <ZhSectionHeading>Kind of work</ZhSectionHeading>
            <p class="text-sm">What would you like help with first?</p>
            <div class="flex flex-wrap gap-2" role="group" aria-label="Kind of work">
                <ZhButton
                    v-for="offered in kinds"
                    :key="offered"
                    variant="ghost"
                    :aria-pressed="kind === offered ? 'true' : 'false'"
                    @click="pickKind(offered)"
                >
                    {{ offered }}
                </ZhButton>
                <ZhButton variant="ghost" :aria-pressed="otherOpen ? 'true' : 'false'" @click="openOther">
                    {{ SOMETHING_ELSE_LABEL }}
                </ZhButton>
            </div>
            <div v-if="otherOpen" class="space-y-2">
                <label class="block text-sm" for="guided-work-other">Say what you need help with</label>
                <Input id="guided-work-other" v-model="otherText" type="text" data-testid="guided-work-other" />
            </div>
            <ZhButton :disabled="!workAnswer" :loading="busy" loading-label="SAVING..." @click="continueWork">
                Continue
            </ZhButton>
            <div v-if="offerSkip">
                <Button type="button" variant="link" data-testid="guided-skip-setup" :disabled="busy" @click="skip">
                    {{ SKIP_SETUP }}
                </Button>
            </div>
        </ZhPanel>

        <ZhPanel v-else-if="state && screen === 'about_you'" class="space-y-4" data-testid="guided-about-you">
            <ZhSectionHeading>About you</ZhSectionHeading>
            <p class="text-sm">What is your role?</p>
            <div class="flex flex-wrap gap-2" role="group" aria-label="Your role">
                <ZhButton
                    v-for="known in WORK_ROLES"
                    :key="known"
                    variant="ghost"
                    :aria-pressed="role?.role === known ? 'true' : 'false'"
                    @click="pickedRole = known"
                >
                    {{ WORK_ROLE_LABELS[known] }}
                </ZhButton>
            </div>
            <div class="space-y-2">
                <label class="block text-sm" for="guided-role-typed">Or type your role</label>
                <Input
                    id="guided-role-typed"
                    :model-value="typedRole"
                    type="text"
                    data-testid="guided-role-typed"
                    @update:model-value="typeRole"
                />
            </div>
            <p v-if="preview" role="status" class="text-sm" data-testid="guided-role-preview">{{ preview }}</p>
            <p class="text-sm">How technical are you?</p>
            <div class="flex flex-wrap gap-2" role="group" aria-label="How technical you are">
                <ZhButton
                    v-for="level in TECHNICAL_LEVELS"
                    :key="level"
                    variant="ghost"
                    :aria-pressed="technicalLevel === level ? 'true' : 'false'"
                    @click="technicalLevel = level"
                >
                    {{ TECHNICAL_LEVEL_LABELS[level] }}
                </ZhButton>
            </div>
            <div class="flex flex-wrap items-center gap-3">
                <ZhButton variant="ghost" :disabled="busy" @click="editingWork = true">Back</ZhButton>
                <ZhButton
                    :disabled="!role && !technicalLevel"
                    :loading="busy"
                    loading-label="SAVING..."
                    @click="continueAboutYou"
                >
                    Continue
                </ZhButton>
            </div>
        </ZhPanel>

        <ZhPanel v-else-if="state && screen === 'team'" class="space-y-4" data-testid="guided-team">
            <ZhSectionHeading>Your team</ZhSectionHeading>
            <p class="text-sm">Pick the team to set up first. You name it on the next screen.</p>
            <div class="grid gap-3" role="group" aria-label="Your team">
                <ZhPanel
                    v-for="offer in offers"
                    :key="offer.id"
                    as="button"
                    type="button"
                    interactive
                    class="w-full space-y-2 text-left"
                    :aria-pressed="pickedTeam === offer.id ? 'true' : 'false'"
                    :data-testid="`guided-team-offer-${offer.id}`"
                    :disabled="busy"
                    @click="pickedTeam = offer.id"
                >
                    <ZhSystemLabel v-if="offer.id === recommendedId" tone="system" as="span">Recommended</ZhSystemLabel>
                    <p class="text-sm font-medium">{{ offer.name }}</p>
                    <p class="text-muted-foreground text-sm">{{ offer.description }}</p>
                    <p class="text-sm">{{ teammateLine(offer) }}</p>
                </ZhPanel>
            </div>
            <p v-if="state.teamProposal?.tellYourCoo" class="text-sm">
                None of these?
                <Button type="button" variant="link" :disabled="busy" data-testid="guided-team-coo" @click="backToChat">
                    {{ TELL_YOUR_COO }}
                </Button>
            </p>
            <ZhButton :disabled="!pickedTeam" :loading="busy" loading-label="SAVING..." @click="continueTeam">
                Continue
            </ZhButton>
        </ZhPanel>

        <ZhPanel v-else-if="state && screen === 'names' && namesFor" class="space-y-4" data-testid="guided-names">
            <ZhSectionHeading>Names</ZhSectionHeading>
            <SetupNamesForm
                :key="namesFor.id"
                :template="namesFor"
                :busy="busy"
                :failure="namesFailure"
                @save="saveNames"
                @skip="saveNames(null)"
            >
                <template #actions>
                    <ZhButton variant="ghost" :disabled="busy" data-testid="guided-names-back" @click="editingTeam = true">
                        Back
                    </ZhButton>
                </template>
            </SetupNamesForm>
        </ZhPanel>

        <OnboardingMoreToolsView v-else-if="state && screen === 'more_tools'" @finished="load" />

        <OnboardingTeamSetupView v-else-if="state && screen === 'team_setup'" embedded />

        <ZhPanel v-else-if="state && screen === 'hand_over'" class="space-y-4" data-testid="guided-hand-over">
            <ZhSectionHeading>{{ handOverTitle }}</ZhSectionHeading>
            <p class="text-sm">
                This step is done with your COO for now. Everything you entered is kept, and you can come back to the
                step-by-step screens afterwards.
            </p>
            <ZhButton :loading="busy" loading-label="OPENING..." @click="backToChat">{{ COO_HAND_OVER }}</ZhButton>
        </ZhPanel>

        <ZhPanel v-else-if="state && screen === 'ai_account'" class="space-y-4" data-testid="guided-ai-account">
            <ZhSectionHeading>AI account</ZhSectionHeading>
            <p class="text-sm">Setup starts by connecting the AI account your team runs on.</p>
            <RouterLink class="underline" :to="ACCOUNT_STEP_PATH">Connect your AI account</RouterLink>
        </ZhPanel>

        <ZhPanel v-else-if="state && screen === 'done'" class="space-y-4" data-testid="guided-done">
            <ZhSystemLabel tone="muted">Setup complete</ZhSystemLabel>
            <RouterLink class="underline" to="/">Go to the dashboard</RouterLink>
        </ZhPanel>
    </section>
</template>
